/* The counterparty card's Debts tab — the Payments tab's sibling (`PaymentsTab.js`): the
   counterparty's open debts in the shared embedded `DocsTab` (paging, error + retry, first-load
   skeleton). Its section heading (`ReadOnlyTag`, `TabHead` saying "open debts" in place of a date
   window, no "All …" button) is `tabParts.js`.

   No window: `excalibur/debts/` returns only the debts still in force (a removed one never comes
   back), already ordered by `planned_payment_date`, earliest first — so the most overdue is on top.
   No summary either: the endpoint sends none.

   A row opens nothing (`clickableRows={false}`). `highlightId` is the debt a payment's "Pays off
   debt" link pointed at (`?tab=debts&debt=<id>`, read by `Page.js`): `DocsTab` scrolls to it and
   washes it briefly if it is on the current page, and does nothing if it is not. */

import React from "react";
import { Typography } from "antd";

import { dash } from "../../component/table-cells";
import { COUNTERPARTIES_PAGE } from "../../config/lang";
import dayjs from "../../util/dayjs";
import { formatMoney } from "../../util/formatter";
import { translate } from "../../util/util";

import DocsTab from "./DocsTab";
import { FillCell } from "./tabParts";

export default function DebtsTab({ counterpartyId, lang, highlightId }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);

  /* Whole days past the due date, against the BROWSER's today: a debt due today is not overdue. */
  const today = dayjs().startOf("day");
  const overdueDays = row => (row.planned_payment_date ? today.diff(dayjs(row.planned_payment_date), "day") : 0);

  /* The Payments tab's content-sized layout: the document takes what its name needs (up to the
     `lp-fit` 280px cap, then an ellipsis), the date and the two numbers keep pixel widths, and the
     comment — last, in a `FillCell` — takes the card's spare room. */
  const columns = [
    {
      key: "document",
      title: t("d_document"),
      render: (v, row) =>
        row.name_of_the_document ? <Typography.Text ellipsis={{ tooltip: row.name_of_the_document }}>{row.name_of_the_document}</Typography.Text> : dash,
    },
    {
      key: "planned_payment_date",
      title: t("d_due"),
      width: 130,
      render: (v, row) => (row.planned_payment_date ? <span className="lp-num">{dayjs(row.planned_payment_date).format("DD.MM.YYYY")}</span> : dash),
    },
    {
      key: "amount",
      title: t("doc_amount"),
      width: 130,
      align: "right",
      render: (v, row) => <span className="lp-num">{formatMoney(row.amount) || dash}</span>,
    },
    {
      /* `--danger-700` text (`.cp-neg`), never `-500`: this is text (spec/01's split). The number
         and its unit say it too, so colour is not the only signal. */
      key: "overdue",
      title: t("d_overdue"),
      width: 120,
      align: "right",
      render: (v, row) => {
        const days = overdueDays(row);
        return days > 0 ? <span className="lp-num cp-neg">{t("d_days").replace("{n}", days)}</span> : dash;
      },
    },
    {
      key: "comment",
      title: t("doc_comment"),
      render: (v, row) =>
        row.comment ? (
          <FillCell full={row.comment} type="secondary">
            {row.comment}
          </FillCell>
        ) : (
          dash
        ),
    },
  ];

  return (
    <DocsTab
      counterpartyId={counterpartyId}
      endpoint="excalibur/debts/"
      columns={columns}
      emptyText={t("debts_empty")}
      lang={lang}
      clickableRows={false}
      highlightId={highlightId}
      /* Content-sized, as the Payments tab. */
    />
  );
}
